import {
  clearEstimateToken,
  getClientFacingMessage,
  MY_ESTIMATE_REQUEST_STATUS_LABELS,
  readEstimateToken,
  useDeclinePublicQuoteMutation,
  useDownloadPublicQuotePdfMutation,
  useGetMyEstimatesQuery,
  useGetPublicRequestQuery,
  useGrantEstimateAccessMutation,
  useIssueQuoteTokensMutation,
  useRequestPublicQuoteModificationMutation,
} from '@massilia/queries'
import { Button, downloadBlob, StatusBadge } from '@massilia/ui'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Loader2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'

import { PersonalDataAccessForm } from '@/components/access/personal-data-access-form'
import { AcceptEstimateDialog } from '@/components/estimates/accept-estimate-dialog'
import { DeclineEstimateDialog } from '@/components/estimates/decline-estimate-dialog'
import {
  REQUEST_BADGE_ICONS,
  REQUEST_BADGE_TONES,
} from '@/components/estimates/estimate-request-summary'
import { EstimateResponseCard } from '@/components/estimates/estimate-response-card'
import { RequestModificationDialog } from '@/components/estimates/request-modification-dialog'
import { RequestStatusNotice } from '@/components/estimates/request-status-notice'
import { useAuthStore } from '@/stores/auth.store'

const searchSchema = z.object({
  /** Secret du message. Retiré de l'URL dès qu'il a été échangé. */
  t: z.string().optional(),
})

/**
 * Page publique d'une DEMANDE.
 *
 * Elle existe pour que les liens des messages n'aboutissent jamais à un écran de
 * connexion : les notifications tarifaires parlent d'une demande **sans aucune
 * proposition**, qui n'avait donc pas de page publique où atterrir. Depuis sa
 * boîte mail, le client butait sur une porte (ACCES-PERSO-LIENS).
 *
 * Le jeton n'ouvre que cette demande — jamais le compte. Ses propositions sont
 * ouvertes par des jetons dérivés, ce qui laisse réutiliser telles quelles les
 * actions de la page publique d'une proposition.
 */
export const Route = createFileRoute('/_blue_header/demande/$estimateId')({
  component: PublicRequestPage,
  validateSearch: searchSchema,
})

const ACCESS_ERROR = 'Demande introuvable ou nom incorrect.'

export function PublicRequestPage() {
  const { estimateId } = Route.useParams()
  const { t } = Route.useSearch()
  const navigate = useNavigate()

  const { isAuthenticated } = useAuthStore()
  const { data: myEstimates } = useGetMyEstimatesQuery(isAuthenticated)

  const [hasAccess, setHasAccess] = useState(() => readEstimateToken(estimateId) !== null)
  const [accessError, setAccessError] = useState<null | string>(null)
  const [dialog, setDialog] = useState<null | {
    kind: 'accept' | 'decline' | 'modification'
    responseId: string
  }>(null)

  const grantAccess = useGrantEstimateAccessMutation()
  const issueQuoteTokens = useIssueQuoteTokensMutation()
  const decline = useDeclinePublicQuoteMutation()
  const requestModification = useRequestPublicQuoteModificationMutation()
  const downloadPdf = useDownloadPublicQuotePdfMutation()

  const { data, error, isLoading } = useGetPublicRequestQuery(estimateId, hasAccess)

  /**
   * Le secret du message est échangé UNE fois, puis retiré de l'URL : un lien
   * recopié ou partagé après coup ne doit pas rouvrir la demande tout seul.
   *
   * La ref garde la PROMESSE, pas seulement le secret — les rappels passés à
   * `mutate` sont abandonnés si le composant se démonte avant la réponse, ce que
   * fait React en développement. Le jeton finissait en session mais l'accès
   * restait faux : le client voyait le mur alors que son lien était bon.
   */
  const exchange = useRef<null | { promise: Promise<unknown>; token: string }>(null)
  useEffect(() => {
    if (!t) return

    if (exchange.current?.token !== t) {
      exchange.current = {
        promise: grantAccess.mutateAsync({ estimateId, token: t }),
        token: t,
      }
    }

    let abandoned = false
    void exchange.current.promise
      .then(() => !abandoned && setHasAccess(true))
      .catch(() => !abandoned && setAccessError(ACCESS_ERROR))
      .finally(() => !abandoned && void navigate({ replace: true, search: {}, to: '.' }))

    return () => {
      abandoned = true
    }
  }, [estimateId, grantAccess, navigate, t])

  /**
   * Dès l'accès obtenu, on range les jetons des propositions : sans eux, les
   * boutons seraient là mais chaque geste répondrait 404.
   *
   * Un échec ne peut donc pas rester muet : la garde compte les tentatives
   * plutôt que la seule demande, sinon un unique POST raté — coupure réseau,
   * 5xx — condamnait la page jusqu'au rechargement complet, chaque geste
   * répondant « impossible d'enregistrer » sans jamais dire pourquoi.
   */
  const [tokenAttempt, setTokenAttempt] = useState(0)
  const derived = useRef<null | string>(null)
  useEffect(() => {
    const attempt = `${estimateId}#${tokenAttempt}`
    if (!hasAccess || derived.current === attempt) return

    derived.current = attempt
    issueQuoteTokens.mutate(estimateId, {
      onError: () =>
        toast.error('Vos propositions n’ont pas pu être préparées.', {
          action: { label: 'Réessayer', onClick: () => setTokenAttempt((count) => count + 1) },
          duration: Infinity,
        }),
    })
  }, [estimateId, hasAccess, issueQuoteTokens, tokenAttempt])

  // Un client connecté à qui appartient la demande n'a rien à faire ici : son
  // espace la lui montre avec toutes les autres.
  useEffect(() => {
    if (!myEstimates?.some((group) => group.id === estimateId)) return

    void navigate({ hash: estimateId, replace: true, to: '/mes-devis' })
  }, [estimateId, myEstimates, navigate])

  // Un jeton périmé n'a pas à laisser la page en chargement : on le purge, et le
  // rendu repasse aussitôt par le mur du nom — `tokenExpired` ci-dessous. Purger
  // sans toucher à l'état : la remise à zéro se déduit de l'erreur, elle n'a pas
  // à être recopiée dans deux `useState` au prix d'un rendu en cascade.
  useEffect(() => {
    if (!error) return

    clearEstimateToken(estimateId)
  }, [error, estimateId])

  const tokenExpired = Boolean(error)

  if (!hasAccess || tokenExpired) {
    return (
      <PersonalDataAccessForm
        description="Pour y accéder, indiquez le nom de famille du client ou d’un voyageur."
        errorMessage={tokenExpired ? ACCESS_ERROR : accessError}
        isSubmitting={grantAccess.isPending}
        onSubmit={(credentials) =>
          grantAccess.mutate(
            { ...credentials, estimateId },
            {
              // Le message du serveur, quand il en a rédigé un : un calcul raté
              // se dit, il ne se confond pas avec un nom inconnu.
              onError: (failure) => setAccessError(getClientFacingMessage(failure, ACCESS_ERROR)),
              onSuccess: () => {
                setAccessError(null)
                setHasAccess(true)
              },
            }
          )
        }
        submitLabel="Voir ma demande"
        title="Votre demande de voyage"
      />
    )
  }

  if (isLoading || !data) {
    return (
      <div className="flex flex-1 items-center justify-center py-14">
        <Loader2 className="size-8 animate-spin text-brand-600" />
      </div>
    )
  }

  const BadgeIcon = REQUEST_BADGE_ICONS[data.status]
  const openResponse = data.responses.find((response) => response.id === dialog?.responseId)
  const liveResponsesCount = data.responses.filter((response) => response.canAct).length

  return (
    <div className="flex w-full flex-col gap-6">
      <header className="flex flex-col gap-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="flex flex-col gap-1">
            <h1 className="text-2xl font-semibold text-gray-light-900">{data.route}</h1>
            <p className="text-sm text-gray-light-600">
              {data.type === 'FLIGHT' ? 'Vol' : 'Séjour'} · {data.passengerCount} voyageur
              {data.passengerCount > 1 ? 's' : ''}
            </p>
          </div>
          <StatusBadge
            appearance={REQUEST_BADGE_TONES[data.status].appearance}
            icon={BadgeIcon ? <BadgeIcon aria-hidden /> : undefined}
            tone={REQUEST_BADGE_TONES[data.status].tone}
          >
            {MY_ESTIMATE_REQUEST_STATUS_LABELS[data.status]}
          </StatusBadge>
        </div>
      </header>

      {/* Même encart que dans l'espace client : le même statut ne s'explique pas
          de deux façons selon la porte par laquelle on est entré. */}
      <RequestStatusNotice group={data} />

      {data.responses.length > 0 && (
        <div className="overflow-hidden rounded-2xl border border-gray-light-200">
          {data.responses.map((response) => (
            <EstimateResponseCard
              key={response.id}
              actions={
                response.canAct
                  ? {
                      onAccept: () => setDialog({ responseId: response.id, kind: 'accept' }),
                      onDecline: () => setDialog({ responseId: response.id, kind: 'decline' }),
                      onDownloadPdf: () =>
                        downloadPdf.mutate(response.id, {
                          onError: () => toast.error('Impossible de télécharger le devis'),
                          onSuccess: (blob) => downloadBlob(blob, `${response.name}.pdf`),
                        }),
                      onRequestModification: () =>
                        setDialog({ responseId: response.id, kind: 'modification' }),
                    }
                  : null
              }
              isDownloadingPdf={downloadPdf.isPending && downloadPdf.variables === response.id}
              response={response}
            />
          ))}
        </div>
      )}

      <aside className="flex flex-col items-start gap-3 rounded-2xl bg-brand-50 p-6 md:flex-row md:items-center md:justify-between">
        <p className="text-sm font-medium text-gray-light-900">
          Vous avez un compte ? Connectez-vous pour retrouver toutes vos demandes et propositions.
        </p>
        <Button asChild className="bg-brand-600 text-white hover:bg-brand-700">
          <Link search={{ redirect: '/mes-devis' }} to="/connexion">
            Se connecter
          </Link>
        </Button>
      </aside>

      <AcceptEstimateDialog
        isOpen={dialog?.kind === 'accept'}
        onConfirm={() => {
          if (!dialog) return

          void navigate({
            params: { estimateResponseId: dialog.responseId },
            to: '/devis/$estimateResponseId/reserver',
          })
        }}
        onOpenChange={(open) => !open && setDialog(null)}
        otherLiveResponsesCount={Math.max(liveResponsesCount - 1, 0)}
        responseName={openResponse?.name ?? ''}
      />

      <DeclineEstimateDialog
        isOpen={dialog?.kind === 'decline'}
        isSubmitting={decline.isPending}
        onConfirm={(payload) =>
          dialog &&
          decline.mutate(
            { ...payload, responseId: dialog.responseId },
            {
              onError: () => toast.error('Impossible d’enregistrer votre refus'),
              onSuccess: () => {
                toast.success('Votre refus a bien été transmis')
                setDialog(null)
              },
            }
          )
        }
        onOpenChange={(open) => !open && setDialog(null)}
        responseName={openResponse?.name ?? ''}
      />

      <RequestModificationDialog
        isOpen={dialog?.kind === 'modification'}
        isSubmitting={requestModification.isPending}
        onConfirm={(payload) =>
          dialog &&
          requestModification.mutate(
            { ...payload, responseId: dialog.responseId },
            {
              onError: () => toast.error('Impossible d’envoyer votre demande'),
              onSuccess: () => {
                toast.success('Votre demande a bien été transmise')
                setDialog(null)
              },
            }
          )
        }
        onOpenChange={(open) => !open && setDialog(null)}
        responseName={openResponse?.name ?? ''}
      />
    </div>
  )
}
